import {
  PaginationState,
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  SortingState,
  useReactTable,
} from "@tanstack/react-table";
import DivLoader from "components/divloader/DivLoader";
import React from "react";
import CreateUserModal from "./CreateUserModal";
import DeleteUserModal from "./DeleteUserModal";
import { UserRowObj } from "..";
import { MdCancel, MdCheckCircle } from "react-icons/md";
import Searchbox from "components/fields/Searchbox";
import Pagination from "components/pagination";
import CustomToolTip from "components/tooltips/new";
import { useNavigate } from "react-router-dom";
import { BsArrowDown, BsArrowUp } from "react-icons/bs";
import { RiArrowUpDownFill } from "react-icons/ri";
import moment from "moment";

const columnHelper = createColumnHelper<UserRowObj>();

function UsersTable(props: {
  tableData: any;
  fetchUsers: () => void;
  isLoading: boolean;
  page: number;
  setPage: any;
  totalpage: number;
  totalItems: number;
  currentPage: number;
  pageSize: number;
  setPageSize: any;
  organizations: any;
  roleData: any;
  roleDataDrop?: any;
  onValueChange: (value: string) => void;
}) {
  const {
    tableData,
    fetchUsers,
    page,
    setPage,
    currentPage,
    totalpage,
    totalItems,
    pageSize,
    setPageSize,
    organizations,
    roleData,
    roleDataDrop,
    onValueChange,
  } = props;

  const navigate = useNavigate();
  const [sorting, setSorting] = React.useState<SortingState>([]);
  const [data, setData] = React.useState(() => [...(tableData ?? [])]);

  React.useEffect(() => {
    setData(tableData ?? []);
  }, [tableData]);

  const handleValueChange = (e: any) => {
    onValueChange(e);
  };

  /* ── Avatar color cycling ── */
  const avatarColors = [
    { bg: "linear-gradient(135deg,#f0fdf4,#dcfce7)", border: "#bbf7d0", color: "#15803d" },
    { bg: "linear-gradient(135deg,#eff6ff,#dbeafe)", border: "#bfdbfe", color: "#2563eb" },
    { bg: "linear-gradient(135deg,#faf5ff,#ede9fe)", border: "#ddd6fe", color: "#7c3aed" },
    { bg: "linear-gradient(135deg,#fffbeb,#fef3c7)", border: "#fde68a", color: "#d97706" },
    { bg: "linear-gradient(135deg,#fff1f2,#ffe4e6)", border: "#fecdd3", color: "#e11d48" },
  ];
  const getAvatarStyle = (index: number) => avatarColors[index % avatarColors.length];

  const columns = [
    columnHelper.accessor("first_name", {
      id: "first_name",
      header: () => <span className="ut-th">Name</span>,
      cell: (info: any) => {
        const style = getAvatarStyle(info.row.index);
        const lastName = info.row.original.last_name;
        const createdAt = info.row.original.created_at;
        const fullName = `${info.getValue()} ${lastName}`;
        return (
          <div className="ut-name-cell">
            <div
              className="ut-user-avatar"
              style={{
                background: style.bg,
                border: `1.5px solid ${style.border}`,
                color: style.color,
              }}
            >
              {info.getValue()?.charAt(0)?.toUpperCase() || "U"}
            </div>
            <div className="ut-name-wrapper">
              <span className="ut-name">{fullName}</span>
              <span className="ut-created-under-name">
                Created: {moment(createdAt, "DD-MM-YYYY HH:mm:ss [GMT]").format("YYYY-MM-DD")}
              </span>
            </div>
          </div>
        );
      },
      enableSorting: true,
    }),
    columnHelper.accessor("email", {
      id: "email",
      header: () => <span className="ut-th">Email</span>,
      cell: (info) => (
        <a href={`mailto:${info.getValue()}`} className="ut-link">
          {info.getValue()}
        </a>
      ),
    }),
    columnHelper.accessor("role", {
      id: "role",
      header: () => <span className="ut-th">Role</span>,
      cell: (info) => (
        <span className="ut-role-badge">{info.getValue()}</span>
      ),
    }),
    columnHelper.accessor("allOrganizations", {
      id: "allOrganizations",
      header: () => <span className="ut-th">Organizations</span>,
      cell: (info) => {
        const orgList: any = info.row.original.allOrganizations || [];
        const orgCount = orgList.length;
        return (
          <CustomToolTip
            content={
              orgCount > 0 ? (
                <div className="ut-tooltip-grid">
                  {orgList.map((org: any, index: any) => (
                    <span
                      key={index}
                      className="ut-tooltip-chip ut-tooltip-chip--clickable"
                      onClick={() =>
                        navigate("/admin/organizations", {
                          state: { search: org.name },
                        })
                      }
                    >
                      {org.name}
                    </span>
                  ))}
                </div>
              ) : (
                <span className="ut-no-data">No Organizations</span>
              )
            }
            trigger={
              <div className={`ut-count-pill ut-count-pill--purple${orgCount === 0 ? " ut-count-pill--empty" : ""}`}>
                <span className="ut-count-dot ut-count-dot--purple" />
                <span>{orgCount}</span>
                {orgCount > 0 && (
                  <svg width="10" height="10" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2.5" fill="none">
                    <polyline points="6 9 12 15 18 9" />
                  </svg>
                )}
              </div>
            }
            header="Assigned Organizations"
          />
        );
      },
      sortingFn: (rowA, rowB) => {
        const a = (rowA.original.allOrganizations || []).length;
        const b = (rowB.original.allOrganizations || []).length;
        return a - b;
      },
      enableSorting: true,
    }),
    columnHelper.accessor("auth_2fa", {
      id: "auth_2fa",
      header: () => <span className="ut-th">2FA</span>,
      cell: (info) =>
        info.getValue() ? (
          <span className="ut-status-badge ut-status-badge--enabled">
            <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="20 6 9 17 4 12" />
            </svg>
            Enabled
          </span>
        ) : (
          <span className="ut-status-badge ut-status-badge--disabled">
            <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
              <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
            </svg>
            Disabled
          </span>
        ),
    }),
    columnHelper.accessor("id", {
      id: "id",
      header: () => <span className="ut-th">Actions</span>,
      cell: (info) => (
        <div className="ut-actions">
          <CreateUserModal
            fetchUsers={fetchUsers}
            id={info.getValue()}
            data={info.row?.original}
            organizations={organizations}
            roleDataDrop={roleDataDrop}
            roleData={roleData}
            infoData={info}
          />
          <CreateUserModal
            fetchUsers={fetchUsers}
            id={info.getValue()}
            is_info={true}
            data={info.row?.original}
            organizations={organizations}
            roleDataDrop={roleDataDrop}
            roleData={roleData}
            infoData={info}
          />
          {roleData?.[0]?.user?.value?.delete_user && (
            <DeleteUserModal
              fetchUsers={fetchUsers}
              id={info.getValue()}
              name={
                info?.row?.original?.first_name +
                " " +
                info?.row?.original?.last_name || ""
              }
            />
          )}
        </div>
      ),
      enableSorting: false,
    }),
  ];

  const columnsNonAction = columns.slice(0, 5);

  const hasActionPerms =
    roleData?.[0]?.user?.value?.view_user ||
    roleData?.[0]?.user?.value?.edit_user ||
    roleData?.[0]?.user?.value?.delete_user;

  const table = useReactTable({
    data,
    columns: hasActionPerms ? columns : columnsNonAction,
    state: { sorting },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });

  return (
    <div className="ut-card">
      {/* Header */}
      <div className="ut-header">
        <div className="ut-header-left">
          <div className="ut-header-icon">
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
              <circle cx="9" cy="7" r="4" />
              <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
              <path d="M16 3.13a4 4 0 0 1 0 7.75" />
            </svg>
          </div>
          <div>
            <h3 className="ut-title">All Users</h3>
            <p className="ut-subtitle">
              {totalItems > 0
                ? `${totalItems} total user${totalItems !== 1 ? "s" : ""}`
                : "No users found"}
            </p>
          </div>
        </div>
        <div className="ut-search-wrap">
          <Searchbox onSearch={handleValueChange} />
        </div>
      </div>

      {/* Table */}
      <div className="ut-table-wrap">
        {props.isLoading ? (
          <div className="ut-loading">
            <div className="ut-spinner" />
            <p className="ut-loading-txt">Loading users…</p>
          </div>
        ) : (
          <table className="ut-table">
            <thead>
              {table.getHeaderGroups().map((hg) => (
                <tr key={hg.id}>
                  {hg.headers.map((h) => (
                    <th
                      key={h.id}
                      colSpan={h.colSpan}
                      onClick={h.column.getToggleSortingHandler()}
                      className="ut-thead-th"
                    >
                      <div className="ut-th-inner">
                        {flexRender(h.column.columnDef.header, h.getContext())}
                        {h.column.getCanSort() && (
                          <span className="ut-sort-icon">
                            {h.column.getIsSorted() === "asc" ? (
                              <BsArrowUp size={11} />
                            ) : h.column.getIsSorted() === "desc" ? (
                              <BsArrowDown size={11} />
                            ) : (
                              <RiArrowUpDownFill size={11} />
                            )}
                          </span>
                        )}
                      </div>
                    </th>
                  ))}
                </tr>
              ))}
            </thead>
            <tbody>
              {table.getRowModel().rows.length > 0 ? (
                table.getRowModel().rows.map((row, ri) => (
                  <tr
                    key={row.id}
                    className={`ut-tbody-tr${ri % 2 !== 0 ? " ut-tbody-tr--alt" : ""}`}
                  >
                    {row.getVisibleCells().map((cell) => (
                      <td key={cell.id} className="ut-tbody-td">
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </td>
                    ))}
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={8}>
                    <div className="ut-empty">
                      <div className="ut-empty-icon">
                        <svg
                          width="26"
                          height="26"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="1.5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        >
                          <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                          <circle cx="9" cy="7" r="4" />
                          <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
                          <path d="M16 3.13a4 4 0 0 1 0 7.75" />
                        </svg>
                      </div>
                      <p className="ut-empty-title">No users found</p>
                      <p className="ut-empty-sub">
                        Try adjusting your search or add a new user
                      </p>
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        )}
      </div>

      {/* Pagination */}
      <div className="ut-pagination-wrap">
        <Pagination
          setPage={setPage}
          page={page}
          totalpage={totalpage}
          currentPage={currentPage}
          pageSize={pageSize}
          setPageSize={setPageSize}
          arraySize={[50, 100, 200]}
        />
      </div>

      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700;800&display=swap');

        .ut-card {
          font-family: 'Plus Jakarta Sans', sans-serif;
          background: #ffffff;
          border: 1px solid #e2e8f0;
          border-radius: 18px;
          overflow: hidden;
          box-shadow: 0 2px 8px rgba(15,23,42,0.06);
        }

        /* Header */
        .ut-header {
          padding: 20px 24px 16px;
          border-bottom: 1px solid #f1f5f9;
          display: flex; align-items: center; justify-content: space-between; gap: 14px; flex-wrap: wrap;
        }
        .ut-header-left { display: flex; align-items: center; gap: 12px; }
        .ut-header-icon {
          width: 40px; height: 40px; border-radius: 11px;
          background: linear-gradient(135deg, #f0fdf4, #dcfce7);
          border: 1.5px solid #bbf7d0;
          display: flex; align-items: center; justify-content: center;
          color: #16a34a; flex-shrink: 0;
        }
        .ut-title { font-size: 15px; font-weight: 700; color: #0f172a; letter-spacing: -0.2px; margin-bottom: 2px; line-height: 22px; }
        .ut-subtitle { font-size: 12px; color: #94a3b8; font-weight: 500; }
        .ut-search-wrap { min-width: 220px; }

        /* Table */
        .ut-table-wrap { overflow-x: auto; }
        .ut-table-wrap::-webkit-scrollbar { height: 4px; }
        .ut-table-wrap::-webkit-scrollbar-thumb { background: #cbd5e1; border-radius: 4px; }
        .ut-table { width: 100%; border-collapse: collapse; }

        /* Head */
        .ut-thead-th {
          padding: 12px 16px 10px;
          background: #f8fafc;
          border-bottom: 1.5px solid #e2e8f0;
          text-align: left; cursor: pointer; white-space: nowrap; user-select: none;
        }
        .ut-thead-th:hover { background: #f1f5f9; }
        .ut-th-inner { display: flex; align-items: center; gap: 5px; }
        .ut-th { font-size: 11.5px; font-weight: 700; color: #374151; text-transform: uppercase; letter-spacing: 0.5px; }
        .ut-sort-icon { color: #94a3b8; display: flex; align-items: center; transition: color 0.15s; }
        .ut-thead-th:hover .ut-sort-icon { color: #16a34a; }

        /* Rows */
        .ut-tbody-tr { transition: background 0.12s; }
        .ut-tbody-tr:hover { background: #f8fafc; }
        .ut-tbody-tr--alt { background: #fafbfc; }
        .ut-tbody-tr--alt:hover { background: #f1f5f9; }
        .ut-tbody-td { padding: 13px 16px; border-bottom: 1px solid #f1f5f9; vertical-align: middle; }

        /* Name cell */
        .ut-name-cell { display: flex; align-items: center; gap: 9px; }
        .ut-user-avatar {
          width: 32px; height: 32px; border-radius: 9px; flex-shrink: 0;
          display: flex; align-items: center; justify-content: center;
          font-size: 12px; font-weight: 700;
        }
        .ut-name-wrapper { display: flex; flex-direction: column; gap: 2px; }
        .ut-name { font-size: 13px; font-weight: 600; color: #0f172a; white-space: nowrap; }
        .ut-created-under-name { font-size: 11px; color: #94a3b8; font-weight: 500; }
        .ut-link { font-size: 13px; font-weight: 500; color: #2563eb; text-decoration: none; }
        .ut-link:hover { text-decoration: underline; }

        /* Role badge */
        .ut-role-badge {
          display: inline-flex; align-items: center;
          padding: 4px 10px; border-radius: 7px;
          font-size: 11.5px; font-weight: 600;
          background: linear-gradient(135deg,#f0fdf4,#dcfce7);
          border: 1.5px solid #bbf7d0; color: #15803d;
          white-space: nowrap;
        }

        /* 2FA status badges */
        .ut-status-badge {
          display: inline-flex; align-items: center; gap: 5px;
          padding: 4px 10px; border-radius: 7px;
          font-size: 11.5px; font-weight: 600;
        }
        .ut-status-badge--enabled {
          background: linear-gradient(135deg,#f0fdf4,#dcfce7);
          border: 1.5px solid #bbf7d0; color: #15803d;
        }
        .ut-status-badge--disabled {
          background: #f8fafc; border: 1.5px solid #e2e8f0; color: #94a3b8;
        }

        /* Count pills */
        .ut-count-pill {
          display: inline-flex; align-items: center; gap: 5px;
          padding: 5px 11px; border-radius: 8px;
          font-size: 12.5px; font-weight: 700; cursor: pointer;
          transition: all .15s;
        }
        .ut-count-pill--purple {
          background: linear-gradient(135deg,#faf5ff,#ede9fe);
          border: 1.5px solid #ddd6fe; color: #7c3aed;
        }
        .ut-count-pill--purple:hover { background: #ede9fe; border-color: #c4b5fd; }
        .ut-count-pill--empty {
          background: #f8fafc; border: 1.5px solid #e2e8f0; color: #94a3b8; cursor: default;
        }
        .ut-count-dot { width: 6px; height: 6px; border-radius: 50%; flex-shrink: 0; }
        .ut-count-dot--purple { background: #7c3aed; }
        .ut-count-pill--empty .ut-count-dot--purple { background: #cbd5e1; }

        /* Tooltip */
        .ut-tooltip-grid { display: flex; flex-wrap: wrap; gap: 6px; }
        .ut-tooltip-chip {
          font-size: 11.5px; font-weight: 600;
          padding: 4px 8px; border-radius: 6px;
          background: #f1f5f9; border: 1px solid #e2e8f0; color: #374151;
        }
        .ut-tooltip-chip--clickable { cursor: pointer; }
        .ut-tooltip-chip--clickable:hover { background: #ede9fe; border-color: #ddd6fe; color: #7c3aed; }
        .ut-no-data { font-size: 12px; color: #94a3b8; font-style: italic; }

        /* Actions */
        .ut-actions { display: flex; align-items: center; gap: 6px; flex-wrap: wrap; }

        /* Empty */
        .ut-empty {
          display: flex; flex-direction: column; align-items: center;
          justify-content: center; padding: 56px 24px; gap: 10px;
        }
        .ut-empty-icon {
          width: 52px; height: 52px; border-radius: 13px;
          background: #f8fafc; border: 1.5px solid #e2e8f0;
          display: flex; align-items: center; justify-content: center; color: #cbd5e1;
        }
        .ut-empty-title { font-size: 14px; font-weight: 700; color: #64748b; }
        .ut-empty-sub { font-size: 12.5px; color: #94a3b8; font-weight: 500; text-align: center; }

        /* Loading */
        .ut-loading {
          display: flex; flex-direction: column; align-items: center;
          justify-content: center; padding: 56px; gap: 12px;
        }
        .ut-spinner {
          width: 28px; height: 28px;
          border: 3px solid #e2e8f0; border-top-color: #16a34a;
          border-radius: 50%; animation: utSpin 0.7s linear infinite;
        }
        @keyframes utSpin { to { transform: rotate(360deg); } }
        .ut-loading-txt { font-size: 13px; color: #94a3b8; font-weight: 500; }

        /* Pagination */
        .ut-pagination-wrap { padding: 12px 16px; border-top: 1px solid #f1f5f9; background: #fafbfc; }

        @media (max-width: 700px) {
          .ut-header { flex-direction: column; align-items: flex-start; }
          .ut-search-wrap { width: 100%; }
          .ut-thead-th, .ut-tbody-td { padding: 10px 12px; }
        }
      `}</style>
    </div>
  );
}

export default UsersTable;